/**
 * Client entry: boot, join flow, game loop. (§2.3, §3.1, §9.4)
 *
 * ── The loop, and the one rule that governs it ─────────────────────────────
 *
 * The world is advanced ONLY by `step()` from @fg/shared, only ever inside
 * FixedStepDriver, and only through PredictionSystem (§15.2). Nothing in this
 * file writes a player position to "make it look right" — the render layer reads
 * the predicted world for the local player and the interpolated view for
 * everyone else, and those are the only two sources of truth on screen.
 *
 *   rAF frame:
 *     driver.advance(dt)          -> N fixed ticks, each: sample input,
 *                                    predict() (§2.3), queue for send
 *     send input batch            -> newest + redundancy (§2.3)
 *     interp.sample(renderTime)   -> remote players + ball at
 *                                    serverTime - 100ms (§2.5)
 *     camera.update()             -> §7
 *     renderer.render()
 *
 * Snapshots arrive off the loop, on the transport's callback: clock sample →
 * interpolation buffer → reconcile (§2.4).
 *
 * ── Two rendering sources, never mixed ─────────────────────────────────────
 *
 * Local player: predicted world + visual offset (§2.3/§2.4). Zero input latency.
 * Everyone else: interpolated, 100ms in the past (§2.5). Smooth under jitter.
 * The ball switches between them per §2.7 — see `shouldPredictBall`.
 */

import {
  FIXED_DT,
  FixedStepDriver,
  InputFlags,
  MAX_PLAYERS,
  MatchPhase,
  PASS_CHARGE_MAX_S,
  PASS_CHARGE_MIN_S,
  PROTOCOL_VERSION,
  SHOT_CHARGE_MAX_S,
  SHOT_CHARGE_MIN_S,
  choosePassTarget,
  createMatchConfig,
  createPhysicsWorld,
  createWorld,
  type MatchConfig,
  type InputCommand,
  type SquadSize,
} from '@fg/shared/sim';
import type { RuleEvent } from '@fg/shared/sim';
import {
  RejectReason,
  type RefSignalFrame,
  type SnapshotFrame,
  type SnapshotHeader,
  type WelcomePayload,
  applyRosterToWorld,
  buildFingerprint,
  formatFingerprint,
} from '@fg/shared/net';
import { gameWsFallback, joinCodeFromUrl, lobbyUrl } from './config.js';
import { InputManager, buildInputCommand, neutralContext, type InputContext } from './input/InputManager.js';
import type { ControlMap } from './input/controlMap.js';
import { GameConnection } from './net/connection.js';
import { PredictionSystem, ReconcileOutcome, shouldPredictBall } from './net/prediction.js';
import {
  InterpMode,
  InterpolationBuffer,
  ServerClock,
  createInterpolatedView,
  interpModeName,
} from './net/interpolation.js';
import { CameraSystem, cameraModeName, cameraPreset, type CameraInput } from './render/camera.js';
import { Renderer } from './render/renderer.js';
import { resolveBallHold } from './game/ballHold.js';
import { RefSignalKind, refSignalKindFromWire, severityForCard } from './render/refSignal.js';
import { PlayerSwitcher, wireSwitching } from './game/playerSwitch.js';
import { possessionArrowVisible } from './game/possessionArrow.js';
import { applyCollectionAssist } from './game/collectionAssist.js';
import {
  applyCrosshairAim, cameraAimsWithCrosshair, crosshairAimYaw, crosshairArmed, crosshairShown,
} from './game/crosshairAim.js';
import { startPractice } from './game/practice.js';
import { createHudState, mountHud } from './ui/hud.js';
import { mountBanners } from './ui/banners.js';
import { configureMatchNames } from './ui/playerNames.js';
import { createAudioSystem, type AudioTickInput } from './game/audio.js';
import { mountNamePrompt, mountVsCard, type StartScreen } from './ui/menu.js';
/*
 * THE FRONT END IS ui/arena-menu.ts (owner, 2026-08-09: rebuilt from scratch,
 * FIFA-style, easy to navigate). It implements the same TitleFlowHandlers
 * contract ui/menu.ts did, so everything downstream of a mode selection —
 * practice, solo, arena, online, settings — is untouched by the swap.
 *
 * ui/menu.ts is still imported for mountVsCard and mountNamePrompt, which are
 * MATCH-time overlays rather than menu screens, plus the shared MatchSetup type.
 */
import { mountArenaMenu } from './ui/arena-menu.js';
import { getAccessToken } from './account.js';
import { awardMatch } from './economy/award.js';
import { reportCompetitionResult } from './ui/tournament-store.js';
import { mountMenuMusic } from './ui/menuMusic.js';
import { MATCH_ENVIRONMENT, applyTimeOfDay } from './ui/environment.js';
import { mountSettingsScreen, type SettingsScreen } from './ui/settingsScreen.js';
import {
  applyCameraSettings,
  applyInputFeel,
  loadSettings,
  qualityOverride,
  saveSettings,
  type GameSettings,
} from './ui/settings.js';
import { mountDevOverlay, type DevStats } from './devtools/overlay.js';

/**
 * ⚠ Structural mirror of `JoinHandoff` from `@fg/lobby/types`.
 *
 * NOT imported, because `@fg/lobby` is not a dependency of `@fg/client` and
 * adding one means editing package.json + a pnpm install — which would rewrite
 * node_modules underneath the two agents building concurrently in this repo. So
 * this is a deliberate, temporary duplicate of an HTTP DTO.
 *
 * (JSON here is fine and is NOT a §15.4 violation: §15.4 bans JSON on the 60Hz
 * GAME wire. The lobby is a REST API and §9.4's handoff is an HTTP response.)
 *
 * TO FIX: add `"@fg/lobby": "workspace:*"` to packages/client/package.json and
 * `import type { JoinHandoff } from '@fg/lobby/types'`.
 */
interface JoinHandoff {
  roomId: string;
  gameServerUrl: string;
  joinToken: string;
  team: 'A' | 'B';
  slot: number;
  playerId: string;
}

/** §2.3 — every packet carries the newest input plus predecessors. input.ts's
 *  budget note sizes the client's window at 3 (~3.5 KB/s up, inside §2.8's 8). */
const INPUT_REDUNDANCY = 3;
/** §5 middle-mouse CIRCULATE: the safe retain pass fires at a moderate charge. */
const CIRCULATE_PASS_CHARGE = 0.5;
/** Tab: a TAP switches player, a HOLD opens the radar (§4.1 + §4.8). */
const TAB_TAP_MS = 200;
/** RTT probe cadence. Diagnostic only — see connection.ts. */
const PING_INTERVAL_MS = 1000;

// ---------------------------------------------------------------------------
// Boot UI (§9.4) — the title menu lives in ui/menu.ts; this file keeps the
// lobby handoff logic (below) and the match itself. The old bare join box was
// replaced by the menu; its guts (fetchHandoff, incl. the create-friend-match
// flow) are unchanged.
// ---------------------------------------------------------------------------

/**
 * Formation layouts for the position-select pitch, keyed by players-per-team.
 * x/y are percentages of the pitch card (y grows downward; own goal at the
 * bottom, like the classic printed formation chart the owner referenced).
 * `n` is the traditional shirt number shown on the badge; slot index in the
 * lobby == index in this array (slot 0 = GK everywhere it exists).
 */
const FORMATIONS: Readonly<Record<number, readonly { x: number; y: number; n: string }[]>> = {
  1: [{ x: 50, y: 62, n: '9' }],
  2: [{ x: 50, y: 86, n: '1' }, { x: 50, y: 32, n: '9' }],
  3: [{ x: 50, y: 86, n: '1' }, { x: 50, y: 58, n: '4' }, { x: 50, y: 26, n: '9' }],
  4: [{ x: 50, y: 87, n: '1' }, { x: 50, y: 63, n: '4' }, { x: 50, y: 42, n: '8' }, { x: 50, y: 20, n: '9' }],
  5: [
    { x: 50, y: 87, n: '1' },
    { x: 28, y: 63, n: '3' }, { x: 72, y: 63, n: '2' },
    { x: 50, y: 45, n: '8' },
    { x: 50, y: 20, n: '9' },
  ],
  7: [
    { x: 50, y: 88, n: '1' },
    { x: 22, y: 68, n: '3' }, { x: 50, y: 72, n: '4' }, { x: 78, y: 68, n: '2' },
    { x: 34, y: 45, n: '8' }, { x: 66, y: 45, n: '6' },
    { x: 50, y: 20, n: '9' },
  ],
  11: [
    { x: 50, y: 90, n: '1' },
    { x: 81, y: 72, n: '2' }, { x: 62, y: 78, n: '4' }, { x: 38, y: 78, n: '5' }, { x: 19, y: 72, n: '3' },
    { x: 50, y: 61, n: '6' },
    { x: 50, y: 49, n: '8' },
    { x: 50, y: 37, n: '10' },
    { x: 82, y: 27, n: '7' }, { x: 18, y: 27, n: '11' },
    { x: 50, y: 14, n: '9' },
  ],
};

/** Traditional role name for a shirt number — the badge's little caption. */
const ROLE_NAMES: Readonly<Record<string, string>> = {
  '1': 'GK', '2': 'RB', '3': 'LB', '4': 'CB', '5': 'CB', '6': 'CDM',
  '7': 'RW', '8': 'CM', '9': 'ST', '10': 'CAM', '11': 'LW',
};

/** The striped pitch backdrop the badges sit on (pure CSS, no assets). */
function pitchMarkup(inner: string): string {
  return (
    `<div style="position:relative;width:min(88vw,360px);aspect-ratio:3/4.4;border-radius:12px;overflow:hidden;` +
    `background:repeating-linear-gradient(0deg,#3f9d4e 0 11%,#48ab58 11% 22%);border:2px solid rgba(255,255,255,.75);box-shadow:0 12px 40px rgba(0,0,0,.5)">` +
    // centre line + circle, penalty boxes — thin white overlays
    `<div style="position:absolute;left:0;right:0;top:50%;height:2px;background:rgba(255,255,255,.8)"></div>` +
    `<div style="position:absolute;left:50%;top:50%;width:24%;aspect-ratio:1;border:2px solid rgba(255,255,255,.8);border-radius:50%;transform:translate(-50%,-50%)"></div>` +
    `<div style="position:absolute;left:22%;right:22%;top:0;height:13%;border:2px solid rgba(255,255,255,.8);border-top:0"></div>` +
    `<div style="position:absolute;left:36%;right:36%;top:0;height:5.5%;border:2px solid rgba(255,255,255,.8);border-top:0"></div>` +
    `<div style="position:absolute;left:22%;right:22%;bottom:0;height:13%;border:2px solid rgba(255,255,255,.8);border-bottom:0"></div>` +
    `<div style="position:absolute;left:36%;right:36%;bottom:0;height:5.5%;border:2px solid rgba(255,255,255,.8);border-bottom:0"></div>` +
    inner +
    `</div>`
  );
}

/** Mirror of the lobby's RoomDetail DTO (docs/ROOM-LOBBY-SPEC.md). */
interface RoomMemberDto {
  name: string;
  team: 'A' | 'B';
  slot: number;
  confirmed: boolean;
  /** Pressed ENTER MATCH; waiting for the room to release everyone together. */
  ready: boolean;
  isAdmin: boolean;
  isYou: boolean;
}
interface RoomDetailDto {
  id: string;
  mode: string;
  capacity: number;
  playersPerTeam: number;
  status: 'lobby' | 'full' | 'selecting' | 'live';
  players: RoomMemberDto[];
}

/**
 * Human wording for the lobby's error codes — ONE map, read by the join path
 * (a wrong code, a full room) and by the room overlay (a taken seat, a late
 * start) alike, so a player never sees `room-not-found` or `HTTP 409` in the
 * UI. Anything unmapped falls back to the raw code rather than a blank, so a
 * NEW server error is still visible instead of failing silently the way seat
 * picks once did.
 */
const LOBBY_ERROR_TEXT: Record<string, string> = {
  'room-not-found': 'No room with that code — check it with your friend, or it may have expired.',
  'room-full': 'That room is full.',
  'wrong-status': 'That match has already started — ask your friend for a new room.',
  'seat-taken': 'A teammate already has that position — pick another.',
  'team-full': 'That side is full.',
  'not-admin': 'Only the host can start the match.',
  'not-confirmed': 'Pick a position first.',
  'player-not-found': 'You are no longer in this room.',
};
function lobbyErrorText(code: string): string {
  return LOBBY_ERROR_TEXT[code] ?? code;
}

/**
 * A room code as a person types it, made into the code the lobby minted.
 *
 * Codes are upper-case (rooms.ts CODE_ALPHABET) and the lobby's lookup is an
 * exact Map.get — but a code is read aloud across a room or pasted with a
 * stray space, so `9bd4ae` and `9BD4 AE` were both told the room does not
 * exist. Upper-case it and drop anything that is not a letter or digit.
 * Nothing cleverer: the alphabet already omits I/O/0/1 so there is no
 * confusable to map onto, and a wrong length should still say "no room with
 * that code" rather than be quietly padded.
 */
function normalizeRoomCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * The waiting room + position-select screens (docs/ROOM-LOBBY-SPEC.md).
 * Owns one overlay root inside uiEl, polls GET /rooms/:id every 2s, and
 * resolves with the signed handoff once THIS player has picked a seat and
 * entered — or null if they leave. The admin (room creator) gets the START
 * button; everyone can switch teams until selection starts.
 */
async function runRoomLobby(
  uiEl: HTMLElement,
  base: string,
  roomId: string,
  playerId: string,
): Promise<JoinHandoff | null> {
  const root = document.createElement('div');
  root.style.cssText =
    'position:fixed;inset:0;z-index:9999;background:#0a0e0c;color:#e8f5ee;' +
    'font-family:system-ui,sans-serif;display:flex;flex-direction:column;' +
    'align-items:center;padding:32px 16px;overflow:auto';
  uiEl.appendChild(root);

  const post = (action: string, extra: Record<string, unknown> = {}): Promise<Response> =>
    fetchAwake(`${base}/rooms/${encodeURIComponent(roomId)}/${action}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ playerId, ...extra }),
    });

  return new Promise((resolve) => {
    let last: RoomDetailDto | null = null;
    let closed = false;
    /** Last rejection to show the player; cleared by the next success. */
    let notice: string | null = null;
    /**
     * Set once THIS player has pressed ENTER MATCH and the room is still
     * gathering the others. The tally comes from /handoff's pending answer and
     * the screen turns into a "waiting for the room" card until it releases.
     */
    let waiting: { ready: number; total: number; startsInMs: number; since: number } | null = null;

    /**
     * Ask the room for our token. Answers a handoff (go), a pending tally
     * (keep waiting), or a real error. Called on ENTER MATCH and then again on
     * every poll while waiting — the lobby releases everyone the instant the
     * last player is ready, and polling is how each client finds that out.
     */
    const requestHandoff = async (): Promise<void> => {
      const res = await post('handoff');
      if (!res.ok) { await report(res); waiting = null; return; }
      const body = (await res.json()) as JoinHandoff | { waiting: true; ready: number; total: number; startsInMs: number };
      if ('waiting' in body) {
        waiting = { ready: body.ready, total: body.total, startsInMs: body.startsInMs, since: performance.now() };
        return;
      }
      finish(body);
    };

    /** Turn a non-ok lobby response into an on-screen line. */
    const report = async (res: Response): Promise<void> => {
      if (res.ok) { notice = null; return; }
      const body: unknown = await res.json().catch(() => ({}));
      const code = typeof body === 'object' && body !== null
        && typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : `HTTP ${res.status}`;
      notice = lobbyErrorText(code);
    };

    /**
     * Tell the lobby this seat is free. `sendBeacon` rather than fetch because
     * it also runs on `pagehide` — the one moment a normal fetch is not
     * guaranteed to leave the browser. Fire-and-forget: the registry's own
     * MEMBER_STALE_MS sweep is the backstop if this never arrives.
     */
    const leaveUrl = `${base}/rooms/${encodeURIComponent(roomId)}/leave`;
    const leaveBody = JSON.stringify({ playerId });
    const beaconLeave = (): void => {
      // sendBeacon can't set content-type on a string body; a Blob can.
      const blob = new Blob([leaveBody], { type: 'application/json' });
      if (!navigator.sendBeacon?.(leaveUrl, blob)) {
        void fetch(leaveUrl, { method: 'POST', headers: { 'content-type': 'application/json' }, body: leaveBody, keepalive: true }).catch(() => {});
      }
    };
    // A closed tab is a leave. Once we hold a handoff we are at the game
    // server, not the lobby — leaving the lobby then would be a lie the
    // registry refuses anyway (see leaveRoom), so the listener comes off.
    const onPageHide = (): void => { if (!closed) beaconLeave(); };
    window.addEventListener('pagehide', onPageHide);

    const finish = (handoff: JoinHandoff | null): void => {
      if (closed) return;
      closed = true;
      clearInterval(timer);
      window.removeEventListener('pagehide', onPageHide);
      if (handoff === null) beaconLeave(); // walked out on purpose
      root.remove();
      resolve(handoff);
    };

    const render = (): void => {
      if (!last) return;
      const r = last;
      const me = r.players.find((p) => p.isYou);
      const selecting = r.status === 'selecting' || r.status === 'live';

      const chip = (p: RoomMemberDto): string => {
        const initial = (p.name[0] ?? '?').toUpperCase();
        const seat = selecting
          ? (p.confirmed
            ? `<span style="margin-left:auto;font-weight:800;color:#35e07f">${ROLE_NAMES[FORMATIONS[r.playersPerTeam]?.[p.slot]?.n ?? ''] ?? `#${p.slot + 1}`}` +
              (p.ready ? ` <span title="ready">✓</span>` : '') + `</span>`
            : `<span style="margin-left:auto;color:#5b7a68">picking…</span>`)
          : '';
        return (
          `<div style="display:flex;align-items:center;gap:10px;padding:8px 12px;border-radius:8px;margin:6px 0;` +
          `background:${p.isYou ? 'rgba(53,224,127,.16)' : 'rgba(255,255,255,.05)'};border:1px solid ${p.isYou ? '#35e07f55' : 'transparent'}">` +
          `<span style="width:30px;height:30px;border-radius:50%;background:#1c2a22;display:grid;place-items:center;font-weight:800;color:#8fd9b0">${initial}</span>` +
          `<span style="font-weight:${p.isYou ? '800' : '500'}">${p.name}${p.isAdmin ? ' 👑' : ''}${p.isYou ? ' <span style="color:#8fd9b0;font-weight:500">(you)</span>' : ''}</span>` +
          seat +
          `</div>`
        );
      };

      const teamCard = (team: 'A' | 'B'): string => {
        const members = r.players.filter((p) => p.team === team);
        const accent = team === 'A' ? '#4db8ff' : '#ff7a4d';
        return (
          `<div style="flex:1;min-width:240px;max-width:340px;background:linear-gradient(180deg,rgba(255,255,255,.05),rgba(255,255,255,.02));` +
          `border:1px solid #23392d;border-top:3px solid ${accent};border-radius:12px;padding:14px 16px">` +
          `<div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px">` +
          `<span style="font-weight:900;letter-spacing:3px;color:${accent}">TEAM ${team}</span>` +
          `<span style="font-size:11px;color:#5b7a68">${members.length}/${r.capacity / 2}</span></div>` +
          `${members.map(chip).join('') || '<div style="color:#43584c;padding:14px 0;text-align:center;border:1px dashed #23392d;border-radius:8px">waiting for players…</div>'}` +
          `</div>`
        );
      };

      let body = '';
      let controls = '';
      if (!selecting) {
        body =
          `<div style="display:flex;gap:16px;flex-wrap:wrap;justify-content:center;align-items:stretch;width:min(94vw,780px)">` +
          `${teamCard('A')}` +
          `<div style="align-self:center;font-weight:900;font-size:22px;color:#35e07f;text-shadow:0 0 18px #35e07f66">VS</div>` +
          `${teamCard('B')}</div>`;
        controls =
          `<button data-act="copy" style="padding:11px 18px;border-radius:8px;border:1px solid #35e07f;background:none;color:#35e07f;font-weight:700;cursor:pointer">COPY INVITE LINK</button>` +
          `<button data-act="switch" style="padding:11px 18px;border-radius:8px;border:1px solid #4a6a58;background:none;color:#e8f5ee;cursor:pointer">SWITCH TEAM</button>` +
          (me?.isAdmin
            ? `<button data-act="start" style="padding:11px 28px;border-radius:8px;border:0;background:linear-gradient(180deg,#3ff08c,#28c96b);color:#08130c;font-weight:900;letter-spacing:1px;cursor:pointer;box-shadow:0 4px 18px #35e07f44">START ▸</button>`
            : `<div style="color:#8fd9b0;align-self:center;font-size:13px">waiting for the host to start…</div>`);
      } else {
        // Formation pitch: badges at real positions, own goal at the bottom.
        const layout = FORMATIONS[r.playersPerTeam] ?? [];
        const takenBy = new Map<number, RoomMemberDto>();
        for (const p of r.players) {
          if (p.team === me?.team && p.confirmed && !p.isYou) takenBy.set(p.slot, p);
        }
        const penta = 'clip-path:polygon(50% 0,100% 38%,82% 100%,18% 100%,0 38%)';
        let badges = '';
        for (let s = 0; s < layout.length; s++) {
          const pos = layout[s];
          if (!pos) continue;
          const owner = takenBy.get(s);
          const mine = me?.confirmed === true && me.slot === s;
          const role = ROLE_NAMES[pos.n] ?? '';
          const badge = owner
            ? `<div style="width:52px;height:48px;${penta};background:#233028;display:grid;place-items:center;font-size:22px;font-weight:900;color:#5b7a68">${pos.n}</div>` +
              `<div style="font-size:10px;color:#dfe;background:rgba(0,0,0,.55);padding:1px 6px;border-radius:6px;margin-top:2px;max-width:80px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${owner.name}</div>`
            : `<div style="width:52px;height:48px;${penta};background:${mine ? '#35e07f' : '#f4f7f2'};display:grid;place-items:center;font-size:22px;font-weight:900;color:#101510;box-shadow:0 4px 10px rgba(0,0,0,.45)">${pos.n}</div>` +
              `<div style="font-size:10px;font-weight:700;color:#fff;background:rgba(0,0,0,.55);padding:1px 6px;border-radius:6px;margin-top:2px">${mine ? 'YOU · ' : ''}${role}</div>`;
          badges +=
            `<button data-act="seat" data-slot="${s}" ${owner ? 'disabled' : ''} ` +
            `style="position:absolute;left:${pos.x}%;top:${pos.y}%;transform:translate(-50%,-50%);` +
            `background:none;border:0;padding:0;cursor:${owner ? 'default' : 'pointer'};display:flex;flex-direction:column;align-items:center">` +
            `${badge}</button>`;
        }
        body = pitchMarkup(badges);
        if (waiting) {
          // You have pressed ENTER MATCH; the room is gathering the others. The
          // countdown is what stops this reading as a hang — it says exactly
          // when the match starts regardless, and the tally says who is holding
          // it up. Everyone leaves this card at the same instant.
          const left = Math.max(0, Math.ceil((waiting.startsInMs - (performance.now() - waiting.since)) / 1000));
          controls =
            `<div style="display:grid;place-items:center;gap:8px;padding:16px 28px;border-radius:12px;` +
            `background:rgba(53,224,127,.08);border:1px solid #35e07f44;min-width:min(88vw,340px)">` +
            `<div style="width:26px;height:26px;border:3px solid #23392d;border-top-color:#35e07f;border-radius:50%;animation:fg-spin 900ms linear infinite"></div>` +
            `<div style="font-weight:800;letter-spacing:1px">WAITING FOR THE ROOM</div>` +
            `<div style="font-size:22px;font-weight:900;color:#35e07f">${waiting.ready} / ${waiting.total} <span style="font-size:12px;font-weight:600;color:#8fd9b0">ready</span></div>` +
            `<div style="font-size:12px;color:#8fd9b0">Everyone kicks off together — starting in ${left}s either way.</div>` +
            `<style>@keyframes fg-spin{to{transform:rotate(360deg)}}</style>` +
            `</div>`;
        } else {
          controls =
            `<button data-act="enter" ${me?.confirmed ? '' : 'disabled'} style="padding:13px 34px;border-radius:8px;border:0;` +
            `background:${me?.confirmed ? 'linear-gradient(180deg,#3ff08c,#28c96b)' : '#1b2a21'};color:${me?.confirmed ? '#08130c' : '#43584c'};` +
            `font-weight:900;letter-spacing:1px;cursor:${me?.confirmed ? 'pointer' : 'default'};${me?.confirmed ? 'box-shadow:0 4px 18px #35e07f44' : ''}">` +
            `${me?.confirmed ? 'ENTER MATCH ▸' : 'PICK A POSITION FIRST'}</button>`;
        }
      }

      root.innerHTML =
        `<div style="font-size:11px;letter-spacing:4px;color:#8fd9b0;margin-bottom:6px">${selecting ? 'PICK YOUR POSITION' : 'MATCH LOBBY'}</div>` +
        `<div style="font-size:44px;font-weight:900;letter-spacing:10px;line-height:1;margin-bottom:4px;text-shadow:0 0 30px #35e07f33">${r.id}</div>` +
        `<div style="font-size:12px;color:#5b7a68;margin-bottom:20px">${r.mode.toUpperCase()} · ${r.players.length}/${r.capacity} PLAYERS</div>` +
        `<div style="margin-bottom:22px;display:flex;justify-content:center">${body}</div>` +
        `<div style="display:flex;gap:10px;flex-wrap:wrap;justify-content:center">${controls}</div>` +
        (notice
          ? `<div style="margin-top:14px;padding:9px 16px;border-radius:8px;background:rgba(255,122,77,.12);` +
            `border:1px solid #ff7a4d55;color:#ffb599;font-size:13px;max-width:min(90vw,460px);text-align:center">${notice}</div>`
          : '') +
        `<button data-act="leave" style="margin-top:24px;background:none;border:0;color:#5b7a68;cursor:pointer;text-decoration:underline">leave room</button>`;
    };

    root.addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest('button');
      const act = btn?.dataset['act'];
      if (!act) return;
      if (act === 'leave') { finish(null); return; }
      if (act === 'copy') {
        const invite = `${window.location.origin}${window.location.pathname}?join=${encodeURIComponent(roomId)}`;
        void navigator.clipboard?.writeText(invite);
        if (btn) { btn.textContent = 'LINK COPIED ✓'; setTimeout(() => { btn.textContent = 'COPY INVITE LINK'; }, 1500); }
        return;
      }
      void (async () => {
        try {
          // Every branch checks res.ok. Only `enter` used to, so a rejected
          // seat/team/start came back 409 and vanished silently: the player
          // clicked, nothing moved, and nothing said why.
          if (act === 'switch' && last) {
            const me = last.players.find((p) => p.isYou);
            await report(await post('team', { team: me?.team === 'A' ? 'B' : 'A' }));
          } else if (act === 'start') {
            await report(await post('start'));
          } else if (act === 'seat') {
            await report(await post('seat', { slot: Number(btn?.dataset['slot']) }));
          } else if (act === 'enter') {
            await requestHandoff();
            if (closed) return;
          }
          await poll();
        } catch (err) {
          console.error('[room-lobby]', err);
          notice = 'Network hiccup — retrying.';
          render();
        }
      })();
    });

    const poll = async (): Promise<void> => {
      try {
        // While waiting for the room, the handoff request IS the poll: it
        // refreshes the tally and returns the token the moment the room
        // releases. Falls through to the detail fetch on a real error.
        if (waiting) {
          await requestHandoff();
          if (closed) return;
        }
        const res = await fetch(`${base}/rooms/${encodeURIComponent(roomId)}?me=${encodeURIComponent(playerId)}`);
        if (res.status === 404) { finish(null); return; } // room expired
        if (!res.ok) return;
        const data = (await res.json()) as { room: RoomDetailDto };
        const prevMe = last?.players.find((p) => p.isYou);
        last = data.room;
        const me = last.players.find((p) => p.isYou);
        // Two things a poll can reveal that deserve a word, not a silent redraw:
        if (prevMe && !me) {
          // We were dropped — a laptop that slept through MEMBER_STALE_MS, or
          // the room was recreated under us. A frozen screen with buttons that
          // all 409 is the worst answer; say it and let them leave cleanly.
          notice = 'You were removed from this room (connection went quiet). Leave and rejoin with the code.';
        } else if (prevMe && me && !prevMe.isAdmin && me.isAdmin) {
          // The crown just passed to us: the host left. Without this line the
          // START button simply appears where "waiting for the host" was.
          notice = 'The host left — you are the host now. Press START when everyone is in.';
        }
        render();
      } catch { /* transient — next tick retries */ }
    };
    const timer = setInterval(() => void poll(), 2000);
    void poll();
  });
}

/**
 * The rated-queue searching screen (docs/ACCOUNTS-SPEC.md phase 3). Same
 * poll-an-overlay shape as runRoomLobby — its own root inside uiEl, GET
 * /matchmaking/status/:id every 2s — but there is no seat to pick here: the
 * queue itself decides the pairing, so this resolves the moment a handoff
 * exists, or null on cancel / the ticket vanishing (swept as abandoned, or
 * already claimed by a stale poll racing this one).
 */
async function runMatchmakingSearch(
  uiEl: HTMLElement,
  base: string,
  ticketId: string,
  mode: '11v11' | '5v5',
  name: string,
): Promise<JoinHandoff | null> {
  const root = document.createElement('div');
  root.style.cssText =
    'position:fixed;inset:0;z-index:9999;background:#0a0e0c;color:#e8f5ee;' +
    'font-family:system-ui,sans-serif;display:grid;place-items:center;padding:32px 16px';
  uiEl.appendChild(root);

  return new Promise((resolve) => {
    let closed = false;
    let startingVsAi = false;
    const finish = (handoff: JoinHandoff | null): void => {
      if (closed) return;
      closed = true;
      clearInterval(timer);
      // Best-effort: free the ticket immediately rather than waiting out the
      // queue's own abandoned-ticket sweep. Fine if this never lands.
      void fetch(`${base}/matchmaking/cancel/${encodeURIComponent(ticketId)}`, {
        method: 'POST',
      }).catch(() => {});
      root.remove();
      resolve(handoff);
    };

    const render = (waitingS: number, ratingWindow: number): void => {
      if (startingVsAi) return; // that render owns the screen until it settles
      root.innerHTML =
        `<div style="display:grid;place-items:center;gap:16px;text-align:center">` +
        `<div style="width:44px;height:44px;border:3px solid #2a3f33;border-top-color:#ffd94a;` +
        `border-radius:50%;animation:fg-spin 900ms linear infinite"></div>` +
        `<style>@keyframes fg-spin{to{transform:rotate(360deg)}}</style>` +
        `<div style="font-weight:800;font-size:18px;letter-spacing:.02em">Searching for opponents…</div>` +
        `<div style="font-size:13px;color:#9fb8a8">Rating window ±${ratingWindow} · ${waitingS}s</div>` +
        `<div style="display:flex;gap:10px;justify-content:center;margin-top:8px">` +
        `<button data-act="cancel" style="padding:9px 22px;border-radius:8px;` +
        `border:1px solid #4a6a58;background:none;color:#e8f5ee;cursor:pointer">CANCEL</button>` +
        `<button data-act="vsai" style="padding:9px 22px;border-radius:8px;border:0;` +
        `background:linear-gradient(180deg,#3ff08c,#28c96b);color:#08130c;font-weight:800;` +
        `cursor:pointer">PLAY VS AI</button>` +
        `</div>` +
        // No opponent found yet is not a wait worth forcing — leaving now
        // costs nothing (unrated, thanks to the same /quickmatch this reuses)
        // and starts instantly, same as Quick Match always has.
        `<div style="font-size:11px;color:#5f7a6b;max-width:280px">Give up waiting and play against ` +
        `bots instead — this match won't affect your rating.</div>` +
        `</div>`;
    };

    const playVsAi = (): void => {
      if (startingVsAi || closed) return;
      startingVsAi = true;
      clearInterval(timer);
      root.innerHTML =
        `<div style="display:grid;place-items:center;gap:16px;text-align:center">` +
        `<div style="width:44px;height:44px;border:3px solid #2a3f33;border-top-color:#ffd94a;` +
        `border-radius:50%;animation:fg-spin 900ms linear infinite"></div>` +
        `<style>@keyframes fg-spin{to{transform:rotate(360deg)}}</style>` +
        `<div style="font-weight:800;font-size:18px;letter-spacing:.02em">Starting vs AI…</div>` +
        `</div>`;
      void fetchAwake(`${base}/quickmatch`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ mode, playerName: name }),
      })
        .then((res) => res.json())
        .then((data: unknown) => finish(data as JoinHandoff))
        .catch(() => {
          // Fall back to the searching screen rather than strand the player
          // on a dead spinner — they can retry or cancel from there. Needs a
          // FRESH interval, not just one more poll: the old one was cleared
          // the moment this attempt started.
          startingVsAi = false;
          timer = setInterval(() => void poll(), 2000);
          void poll();
        });
    };

    root.addEventListener('click', (ev) => {
      const act = (ev.target as HTMLElement).closest('button')?.dataset['act'];
      if (act === 'cancel') finish(null);
      else if (act === 'vsai') playVsAi();
    });

    const poll = async (): Promise<void> => {
      if (startingVsAi) return;
      try {
        const res = await fetch(`${base}/matchmaking/status/${encodeURIComponent(ticketId)}`);
        if (!res.ok) return;
        const s = (await res.json()) as
          | { status: 'searching'; ratingWindow: number; waitingMs: number }
          | { status: 'matched'; handoff: JoinHandoff }
          | { status: 'not-found' };
        if (s.status === 'matched') { finish(s.handoff); return; }
        if (s.status === 'not-found') { finish(null); return; }
        render(Math.round(s.waitingMs / 1000), s.ratingWindow);
      } catch { /* transient — next tick retries */ }
    };
    let timer = setInterval(() => void poll(), 2000);
    void poll();
  });
}

/** Register in a room's waiting room; creates the room first when asked. */
async function joinWaitingRoom(
  name: string,
  code: string,
  createPrivate: boolean,
  mode = '5v5',
): Promise<{ roomId: string; playerId: string }> {
  const base = lobbyUrl().replace(/\/+$/, '');
  let roomCode = normalizeRoomCode(code);
  if (createPrivate && !roomCode) {
    const createResponse = await fetchAwake(`${base}/rooms`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      // Chosen by the host on the Create Room screen. The lobby derives
      // capacity from it (MATCH_MODES) and squad-remap.ts translates the
      // positions picked here onto the real 11-a-side roster the deployed
      // server actually simulates — the rest of the XI is AI, as ever.
      body: JSON.stringify({ mode, isPrivate: true }),
    });
    const created = (await createResponse.json()) as { room?: { id?: string } };
    if (!createResponse.ok || typeof created.room?.id !== 'string') {
      throw new Error('could not create a friend match');
    }
    roomCode = created.room.id;
    // The lobby screen itself carries the code + COPY INVITE LINK now — no
    // separate overlay needed on top of it.
    window.history.replaceState(null, '', `?join=${encodeURIComponent(roomCode)}`);
  }
  const res = await fetchAwake(`${base}/rooms/${encodeURIComponent(roomCode)}/join`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    // See fetchHandoff — identity is resolved once, here at registration.
    body: JSON.stringify({ playerName: name, accessToken: await getAccessToken() }),
  });
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    // The message here is what the menu shows the player verbatim ("Could
    // not join: …"), so it is the human line, never the wire code — a wrong
    // room code used to read `lobby join failed: room-not-found`.
    const code =
      typeof data === 'object' && data !== null && typeof (data as { error?: unknown }).error === 'string'
        ? (data as { error: string }).error
        : `HTTP ${res.status}`;
    throw new Error(lobbyErrorText(code));
  }
  const joined = data as { playerId: string };
  return { roomId: roomCode, playerId: joined.playerId };
}

/**
 * fetch() against the free-alpha lobby, tolerant of Render's free-tier sleep:
 * an idle service takes ~30-60s to wake, during which requests hang or fail.
 * Instead of surfacing "Failed to fetch" to a player (guide §18), show a
 * "waking the server" card and keep retrying for up to ~90s. The overlay is
 * only ever created when the FIRST attempt fails, so a warm server costs
 * nothing.
 */
async function fetchAwake(input: string, init: RequestInit): Promise<Response> {
  const ATTEMPTS = 18;
  const RETRY_MS = 5000;
  let overlay: HTMLElement | null = null;
  try {
    for (let attempt = 1; ; attempt++) {
      try {
        const res = await fetch(input, init);
        // 5xx while the service boots counts as "still waking"; 4xx is a real
        // answer (bad room code etc.) and must reach the caller.
        if (res.status < 500) return res;
        if (attempt >= ATTEMPTS) return res;
      } catch (err) {
        if (attempt >= ATTEMPTS) throw err;
      }
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.style.cssText =
          'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);z-index:99999;' +
          'background:rgba(10,14,12,.96);border:1px solid #ffd94a;border-radius:10px;' +
          'padding:12px 18px;color:#e8f5ee;font-family:system-ui,sans-serif;text-align:center;' +
          'box-shadow:0 8px 32px rgba(0,0,0,.6)';
        overlay.innerHTML =
          `<div style="font-weight:700;margin-bottom:4px">Starting the Footy Arena server…</div>` +
          `<div style="font-size:12px;color:#c9b96a">Free-alpha servers sleep when idle — this can take up to a minute.</div>`;
        document.body.appendChild(overlay);
      }
      await new Promise((r) => setTimeout(r, RETRY_MS));
    }
  } finally {
    overlay?.remove();
  }
}

/**
 * Fetch the §9.4 handoff for QUICK MATCH — instant seat, no waiting room.
 * Friend matches (create / join-by-code) go through joinWaitingRoom() +
 * runRoomLobby() instead, which mint the handoff after the seat pick.
 */
async function fetchHandoff(name: string): Promise<JoinHandoff> {
  const base = lobbyUrl().replace(/\/+$/, '');
  // Signed in ⇒ the lobby verifies this and attaches the account to the seat,
  // so the finished match lands in the player's history (ACCOUNTS-SPEC phase
  // 2). Undefined for guests, and a guest join is a completely normal join.
  const accessToken = await getAccessToken();
  const res = await fetchAwake(`${base}/quickmatch`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ mode: '5v5', playerName: name, accessToken }),
  });
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const msg =
      typeof data === 'object' && data !== null && typeof (data as { error?: unknown }).error === 'string'
        ? (data as { error: string }).error
        : `HTTP ${res.status}`;
    throw new Error(`lobby join failed: ${msg}`);
  }
  return data as JoinHandoff;
}

/**
 * Cold-start gate for the GAME SERVER socket — the ws half of what fetchAwake
 * does for the lobby's HTTP half (guide §18).
 *
 * THE PROBLEM: `GameConnection.connect()` is one-shot, and the free Render
 * instance hosting the match sleeps after 15 minutes idle. The first join after
 * that spends ~50s waking, during which the WebSocket errors or closes
 * immediately. The player had already been dropped into a black scene by then,
 * so all they saw was "Network error: websocket error" and a dead screen —
 * indistinguishable from the game being broken.
 *
 * THE SHAPE OF THE FIX: keep re-dialling until a Welcome arrives, and say so.
 * The overlay is created lazily after GRACE_MS, so a warm server (~200ms
 * connect) never flashes a loading card on the way in. Retries stop the moment
 * the match is welcomed, or on a REJECT — a bad token is a real answer, and
 * re-dialling it would just spin.
 */
interface ConnectGate {
  /** A connect attempt ended without a Welcome — re-dial, unless we're done. */
  attemptFailed(): void;
  /**
   * The Welcome landed: the server is awake, so stop re-dialling — but hold
   * the card up, because building the world (physics wasm, roster, pitch) is
   * itself a visible pause on a slow device, and dropping to a black scene
   * mid-way would look exactly like the hang this whole gate exists to remove.
   */
  loading(): void;
  /**
   * The world is built but the cast (character models) is still streaming
   * in — castPlayerVisual.ts draws nothing at all until every tier loads, so
   * dropping the card here would swap a loading screen for an empty pitch,
   * then players popping in one by one as tiers finish. Holding the card
   * through this instead costs nothing on a cold start (cast loading starts
   * the moment the Renderer is built, well before `connect()` is even
   * called, so it is usually done by the time the Welcome lands) and only
   * shows on a fast warm connect, where the wording keeps it feeling like
   * forward progress rather than a stall.
   */
  waitingOnSquads(): void;
  /** The match is actually ready, or the server gave a definitive answer. */
  settle(): void;
}

function mountConnectGate(redial: () => void, opts: { immediate?: boolean } = {}): ConnectGate {
  // Warm connects finish inside the grace — no overlay at all. EXCEPT when the
  // caller already knows this is a real transition: a room that has just been
  // released after everyone pressed ENTER MATCH is leaving a busy screen for
  // a black one, and 1.2s of nothing there reads as a hang, not a warm join.
  const GRACE_MS = opts.immediate ? 0 : 1200;
  const SLEEP_HINT_MS = 6000; // past this it is almost certainly a cold start
  const RETRY_MS = 4000;
  const GIVE_UP_MS = 100_000; // Render cold starts are ~50s; allow generous slack

  const startedAt = performance.now();
  let overlay: HTMLElement | null = null;
  let done = false;
  let phase: 'connect' | 'loading' | 'squads' = 'connect';
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let ticker: ReturnType<typeof setInterval> | null = null;

  const elapsedS = (): number => Math.round((performance.now() - startedAt) / 1000);

  const paint = (): void => {
    if (!overlay) return;
    const cold = phase === 'connect' && performance.now() - startedAt > SLEEP_HINT_MS;
    const title =
      phase === 'squads'
        ? 'Taking the pitch…'
        : phase === 'loading'
          ? 'Loading the match…'
          : cold
            ? 'Waking the match server…'
            : 'Connecting to the match…';
    const body =
      phase === 'squads'
        ? 'Kitting out the last few players.'
        : phase === 'loading'
          ? 'Building the pitch and the squads.'
          : cold
            ? 'Free-alpha servers sleep when nobody is playing. The first join wears the wake-up — usually under a minute.'
            : 'Handing you over to the match server.';
    overlay.innerHTML =
      `<div style="display:grid;place-items:center;gap:14px;text-align:center">` +
      `<div style="width:44px;height:44px;border:3px solid #2a3f33;border-top-color:#ffd94a;` +
      `border-radius:50%;animation:fg-spin 900ms linear infinite"></div>` +
      `<div style="font-weight:800;font-size:17px;letter-spacing:.02em">${title}</div>` +
      `<div style="font-size:13px;color:#9fb8a8;max-width:330px;line-height:1.5">${body}</div>` +
      `<div style="font-size:12px;color:#5f7a6b">${elapsedS()}s</div>` +
      `</div>`;
  };

  const ensureOverlay = (): void => {
    if (overlay || done) return;
    overlay = document.createElement('div');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:99998;display:grid;place-items:center;' +
      'background:#0d1117;color:#e8f5ee;font-family:system-ui,sans-serif';
    // The spinner keyframes have to exist somewhere; scoping them to the
    // overlay's own node keeps this helper self-contained.
    const style = document.createElement('style');
    style.textContent = '@keyframes fg-spin{to{transform:rotate(360deg)}}';
    overlay.appendChild(style);
    document.body.appendChild(overlay);
    paint();
    ticker = setInterval(paint, 1000);
  };

  const graceTimer = setTimeout(ensureOverlay, GRACE_MS);

  const teardown = (): void => {
    done = true;
    clearTimeout(graceTimer);
    if (retryTimer) clearTimeout(retryTimer);
    if (ticker) clearInterval(ticker);
    overlay?.remove();
    overlay = null;
  };

  return {
    attemptFailed(): void {
      // Once the server has welcomed us, a close is a genuine mid-match
      // disconnect — the HUD reports it and this gate stays out of the way.
      if (done || phase === 'loading') return;
      // Past the budget there is nothing useful left to say except the truth.
      if (performance.now() - startedAt > GIVE_UP_MS) {
        ensureOverlay();
        if (overlay) {
          if (ticker) clearInterval(ticker);
          overlay.innerHTML =
            `<div style="text-align:center;max-width:340px">` +
            `<div style="font-weight:800;font-size:17px;margin-bottom:8px">Could not reach the match server</div>` +
            `<div style="font-size:13px;color:#9fb8a8;line-height:1.5;margin-bottom:18px">` +
            `It did not answer after ${elapsedS()}s. It may still be starting up — trying again often works.</div>` +
            `<button id="fg-cg-retry" style="padding:9px 18px;border-radius:4px;border:0;background:#ffd94a;font-weight:700;cursor:pointer">RETRY</button>` +
            `&nbsp;<button id="fg-cg-menu" style="padding:9px 18px;border-radius:4px;border:1px solid #666;background:none;color:#fff;cursor:pointer">MENU</button>` +
            `</div>`;
          overlay
            .querySelector('#fg-cg-retry')
            ?.addEventListener('click', () => window.location.reload());
          overlay
            .querySelector('#fg-cg-menu')
            ?.addEventListener('click', () => {
              window.location.href = window.location.pathname;
            });
        }
        done = true; // stop re-dialling, but leave the card up
        clearTimeout(graceTimer);
        if (retryTimer) clearTimeout(retryTimer);
        return;
      }
      ensureOverlay();
      // One pending re-dial at a time: a failed attempt can surface as BOTH
      // onError and onClose, and double-dialling would open two sockets.
      if (retryTimer) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (!done) redial();
      }, RETRY_MS);
    },
    loading(): void {
      if (done) return;
      phase = 'loading';
      // The server answered, so a later close is a real disconnect, not a
      // cold start: cancel any pending re-dial.
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      // World-building can outlast a warm connect that never showed the card,
      // so this is also where a slow *device* gets one.
      ensureOverlay();
      paint();
    },
    waitingOnSquads(): void {
      if (done) return;
      phase = 'squads';
      ensureOverlay();
      paint();
    },
    settle: teardown,
  };
}

/** Map a Welcome onto the MatchConfig the server is simulating (§9.2). */
function configFromWelcome(p: WelcomePayload): MatchConfig {
  // The server is authoritative for config: the client LEARNS it here rather
  // than choosing it (welcome.ts). createMatchConfig seeds the squad-size
  // preset; every field the Welcome carries then overrides it, because a
  // preset default that disagrees with the server is a silent desync.
  return createMatchConfig(p.squadSize as SquadSize, {
    hasGoalkeepers: p.hasGoalkeepers,
    offsideEnabled: p.offsideEnabled,
    foulsEnabled: p.foulsEnabled,
    wetPitch: p.wetPitch,
    varEnabled: p.varEnabled,
    airDensity: p.airDensity,
    halfLengthS: p.halfLengthS,
    substitutionsPerTeam: p.substitutionsPerTeam,
    seed: p.seed,
  });
}

function rejectText(r: RejectReason): string {
  switch (r) {
    case RejectReason.BadToken: return 'Invalid join token.';
    case RejectReason.Expired: return 'Join token expired — rejoin from the lobby.';
    case RejectReason.Malformed: return 'Malformed handshake.';
    case RejectReason.RoomFull: return 'Room is full.';
    case RejectReason.SlotTaken: return 'That seat is already taken.';
    case RejectReason.ProtocolMismatch: return 'Version mismatch — refresh the page.';
    case RejectReason.RoomClosed: return 'Room closed.';
    default: return 'Connection rejected.';
  }
}

// ---------------------------------------------------------------------------
// Match
// ---------------------------------------------------------------------------

async function startMatch(
  sceneEl: HTMLElement,
  uiEl: HTMLElement,
  handoff: JoinHandoff,
  settings: GameSettings,
  opts: { fromRoom?: boolean } = {},
): Promise<void> {
  const hud = createHudState();
  const hudHandle = mountHud(uiEl, hud);
  hud.status = 'Connecting…';
  // Broadcast event banners (GOAL/OFFSIDE/PENALTY/HT/FT). Online they run on
  // the phase feed only — rule events live on the server (banners.ts header) —
  // plus §5 team-command feedback via pushText.
  // Name the cast for the broadcast overlays (ui/playerNames.ts). Online the
  // squad size arrives with the world, so this is re-run below once the first
  // snapshot has been applied; 11 is the right guess until then.
  configureMatchNames(11);
  const banners = mountBanners(uiEl, hud);
  // Procedural gameplay audio (§11). Online the sim runs on the server, so this
  // used to have NO rule events at all and the whistle could only be inferred
  // from a phase edge (never firing for a throw-in / corner / free kick). The
  // server now sends the referee's own signals as their own tiny frame
  // (MessageType.RefereeSignal, §5.6) and `netRuleEvents` below hands them to the
  // SAME event feed practice.ts uses — so online and offline whistle alike.
  const audio = createAudioSystem();
  audio.setMasterVolume(settings.masterVolume);

  const renderer = new Renderer(sceneEl, qualityOverride(settings));
  window.addEventListener('resize', () => renderer.resize());

  // Pointer lock must target the element the canvas lives in, not the canvas
  // itself: the canvas is recreated on a renderer rebuild, which would silently
  // drop the lock and with it all aim input.
  const input = new InputManager(sceneEl, settings.controls);
  input.attach();
  applyInputFeel(input, settings); // recovered mouse/aim sensitivity (§7.1)
  sceneEl.addEventListener('click', () => {
    if (!settingsScreen) input.requestPointerLock();
    audio.resume(); // browsers block autoplay until a real user gesture (§11)
  });

  // ESC (when the pointer is not locked — the browser eats the locked ESC to
  // exit the lock first) toggles the settings screen. An online match cannot
  // freeze (§9.3), so unlike practice this overlays WITHOUT pausing; camera
  // sliders preview live per §7.1.
  let settingsScreen: SettingsScreen | null = null;
  const toggleSettings = (): void => {
    if (settingsScreen) {
      settingsScreen.dispose();
      settingsScreen = null;
      return;
    }
    // Mount BEFORE releasing the lock, not after. releasePointerLock() below
    // can fire 'pointerlockchange' synchronously in some browsers/automation
    // contexts, and that listener (below) re-enters toggleSettings() guarded
    // only by `!settingsScreen` — assign the variable first so a same-tick
    // reentrant call sees it as already open and disposes instead of
    // mounting a second overlapping `.fgset` panel.
    settingsScreen = mountSettingsScreen(uiEl, {
      settings,
      onChange: (s) => saveSettings(s),
      onClose: () => {
        settingsScreen?.dispose();
        settingsScreen = null;
      },
      live: { camera, renderer, input, audio },
    });
    input.releasePointerLock();
  };

  // Lock can be lost for reasons other than our own ESC→toggleSettings path —
  // alt-tab, a browser-forced release, the OS notification banner — and unlike
  // practice.ts (which has a real pause to fall back to, §9.3) an online match
  // just keeps running. Surface the SAME settings overlay ESC already opens
  // rather than leaving the cursor silently unlocked with no affordance to
  // relock: the guard on `settingsScreen` means this never double-opens when
  // toggleSettings's OWN releasePointerLock() call is what triggered the event.
  document.addEventListener('pointerlockchange', () => {
    if (document.pointerLockElement !== sceneEl && !settingsScreen) toggleSettings();
  });

  const camera = new CameraSystem();
  const clock = new ServerClock();
  const view = createInterpolatedView();
  const ctx: InputContext = neutralContext();
  const driver = new FixedStepDriver();

  // Everything below is null until the Welcome tells us what match this is.
  let prediction: PredictionSystem | null = null;
  let interp: InterpolationBuffer | null = null;
  let switcher: PlayerSwitcher | null = null;
  let config: MatchConfig | null = null;

  // Redundancy window, newest-first (§2.3). Fixed length, reused every tick.
  const sendWindow: InputCommand[] = [];
  /**
   * §5.6 referee signals that arrived off-loop (on the transport callback) and
   * have not yet been handed to the audio layer. Declared before `conn` because
   * `onRefereeSignal` closes over it.
   *
   * WHY A QUEUE and not a direct call: `audio.update` is driven once per SIM TICK
   * from inside `driver.advance`, and a snapshot/signal frame arrives whenever the
   * socket says so. Playing the whistle straight from the callback would work, but
   * it would be a second, online-only path into the audio system that could drift
   * from `decideAudioFrame`'s event feed. Queue-then-drain means online whistles
   * go through the identical code offline ones do.
   */
  const netRuleEvents: RuleEvent[] = [];
  let pendingFlags = 0;
  let msSinceLocalKick = -1;
  let lastKickAtMs = -1;
  let lastPassTarget = -1; // §13 pass-target hysteresis: the currently-highlighted receiver
  /**
   * THE AIM (game/crosshairAim.ts): the world heading the screen-centre reticle
   * points along, refreshed from the live camera pose each frame. NaN until the
   * first pose exists.
   *
   * ONLINE NOTE: this rewrites `cmd.aimYaw`, which is on the wire, BEFORE
   * prediction.predict() stamps it and before it is sent — exactly like the
   * pass-to-highlight override below. So the server's authoritative strike and
   * the local prediction resolve the same heading and there is nothing to
   * reconcile; no sim change, no wire change.
   */
  let crosshairYaw = Number.NaN;

  // Diagnostics.
  let fps = 60;
  let corrections = 0;
  let correctionsPerSec = 0;
  let lastCorrectionWindow = performance.now();

  const conn = new GameConnection({
    url: handoff.gameServerUrl || gameWsFallback(),
    token: handoff.joinToken,
    events: {
      onWelcome: (p: WelcomePayload): void => {
        config = configFromWelcome(p);
        // Online HUD shows BOTH fingerprints. The connection already rejected any
        // gameplay-critical mismatch before this fires, so an accepted Welcome
        // means they agree — display them side by side, unhighlighted.
        hud.localFingerprint = formatFingerprint(buildFingerprint());
        hud.serverFingerprint = formatFingerprint({
          gitCommit: p.gitCommit,
          protocolVersion: p.protocolVersion,
          simulationVersion: p.simulationVersion,
          constantsHash: p.constantsHash,
        });
        hud.fingerprintMismatch = false;
        hud.status = 'Loading physics…';
        connectGate.loading();
        void bootWorld(p, config).finally(() => {
          // Cast loading (render/player/castPlayerVisual.ts) started the
          // moment `renderer` was constructed, well before `connect()` was
          // even called — so on a real connect (let alone a cold-started
          // one) it is normally finished by now, and this settles right
          // away same as before. It only visibly polls on a fast warm
          // connect, and holding the card an extra beat there costs far
          // less than the alternative: revealing an empty pitch, or
          // players popping in one by one, under it.
          if (renderer.castReady) {
            connectGate.settle();
            return;
          }
          connectGate.waitingOnSquads();
          const poll = window.setInterval(() => {
            if (!renderer.castReady) return;
            window.clearInterval(poll);
            connectGate.settle();
          }, 100);
        });
      },
      onSnapshot: (frame: SnapshotFrame, header: SnapshotHeader): void => {
        if (!prediction || !interp) return;
        clock.sample(header.tick, performance.now());
        interp.push(frame);
        const stats = prediction.reconcile(frame, header.lastProcessedInputSeq);
        if (stats.outcome !== ReconcileOutcome.Clean) corrections++;
        hud.status = '';
      },
      /**
       * §5.6 — THE REFEREE, ONLINE. The server sends its own signals (whistle /
       * yellow / red) as edges, because no snapshot can carry them: the whistle
       * is the restart's Setup→Ready flip and snapshots are 20Hz deltas, and a
       * card is shown once at the offence. See shared/net/refsignal.ts.
       *
       * Three consumers, all presentation:
       *   1. the 3D referee (render/referee.ts) — he jogs to the spot and shows
       *      the cue, via RenderState.refSignal;
       *   2. the whistle sound — queued for the next sim tick so it rides the
       *      SAME `decideAudioFrame` event feed offline uses, rather than a
       *      second online-only code path that could drift from it;
       *   3. the card banner — online never had one, because banners.ts's phase
       *      watcher can see a FREE KICK but not the booking that caused it.
       *
       * `signal` is borrowed (connection.ts), so everything is read here and now.
       */
      onRefereeSignal: (signal: RefSignalFrame): void => {
        const kind = refSignalKindFromWire(signal.kind);
        if (kind === RefSignalKind.None) return;
        renderer.signalReferee(kind, signal.tick, signal.x, signal.z, signal.phase);
        // A few objects a minute, off the 60Hz path — §15.7 is about the steady
        // frame loop, and re-using the shapes banners/audio already consume is
        // worth far more here than avoiding these allocations.
        if (kind === RefSignalKind.Whistle) {
          netRuleEvents.push({
            type: 'WHISTLE',
            tick: signal.tick,
            team: signal.team,
            player: signal.player,
            phase: signal.phase,
            x: signal.x,
            z: signal.z,
          });
          return;
        }
        // A card. Turned back into the FOUL event shape banners.ts and audio.ts
        // already understand (severityForCard is the documented inverse of the
        // severity→card mapping the server applied).
        const foul: RuleEvent = {
          type: 'FOUL',
          tick: signal.tick,
          team: signal.team,
          player: signal.player,
          severity: severityForCard(kind),
          x: signal.x,
          z: signal.z,
        };
        netRuleEvents.push(foul);
        banners.pushEvents([foul]);
      },
      onReject: (r: RejectReason): void => {
        // A definitive answer from a server that is plainly awake. Re-dialling
        // a rejected token just spins, so the gate stops here.
        connectGate.settle();
        hud.status = rejectText(r);
      },
      onVersionMismatch: (detail): void => {
        // §12.6 / §15.2: never play on through a version skew — protocol, sim
        // behaviour OR a gameplay tuning difference. The desyncs it causes are
        // silent and cost days to diagnose. Surface it VISIBLY (not just a log)
        // and highlight the two fingerprints so the difference is legible.
        hud.status = detail.message;
        hud.localFingerprint = formatFingerprint(detail.local);
        hud.serverFingerprint = formatFingerprint(detail.server);
        hud.fingerprintMismatch = true;
        console.error('[net] refusing to play:', detail.reason, detail);
      },
      onClose: (reason: string): void => {
        hud.status = `Disconnected: ${reason}`;
        // Before the Welcome this is a cold start, not a disconnect — the
        // gate re-dials. After it, it is a genuine drop and settle() is a
        // no-op, so the HUD message above stands.
        connectGate.attemptFailed();
      },
      onError: (err: Error): void => {
        hud.status = `Network error: ${err.message}`;
        connectGate.attemptFailed();
      },
    },
  });

  const connectGate = mountConnectGate(() => conn.connect(), { immediate: opts.fromRoom === true });

  /**
   * Build the client's world once the server has told us what it is simulating.
   * The Welcome carries the full roster (attributes/positions/roles) and the
   * switching capability — both MUST be applied here or online play desyncs:
   * without the roster the prediction runs a neutral 50-across squad (steady
   * low-grade corrections at RTT); without wireSwitching the server's
   * ControlAssign reassignments are silently dropped, so after the first
   * auto-switch the human's inputs drive one player while the client renders
   * and predicts another (observed as permanent correction churn + a camera
   * following the wrong man).
   */
  async function bootWorld(p: WelcomePayload, cfg: MatchConfig): Promise<void> {
    const template = createWorld(cfg);
    // The wire roster BEFORE PredictionSystem — prediction must simulate the
    // same attributes/positions the server does (playerSwitch.ts header).
    applyRosterToWorld(template, p.roster);
    const physics = await createPhysicsWorld(cfg);
    physics.configurePitch(cfg);
    // physics-rapier.ts's caller requirement: syncAndStep once before the first
    // step(), or the capsules sit at the origin and moveCharacter is nonsense.
    physics.syncAndStep(template);

    prediction = new PredictionSystem({ template, physics, localPlayerId: p.assignedPlayerId });
    interp = new InterpolationBuffer(template);
    switcher = new PlayerSwitcher({
      initialPlayerId: p.assignedPlayerId,
      team: p.team,
      // §4.8 — the server gates switching (off for a full-human team) and
      // drives reassignment via ControlAssign; wireSwitching below routes each
      // one into prediction (rebase) + the switcher (camera blend).
      enabled: p.switchingEnabled,
    });
    wireSwitching({ connection: conn, prediction, switcher });

    renderer.setPitch(cfg);
    // Owner, 2026-08-15: "please keep the stadium same for all modes" —
    // online never called this at all (only practice.ts did, off the local
    // Match Setup screen's choice), so it silently sat on lighting.ts's
    // hardcoded 'day' default. Time of day is presentation-only (§15.2 —
    // never sim state, never sent over the wire), so the honest per-player
    // choice here is the SAME persisted local preference solo now also
    // uses, not something the server has any business dictating.
    MATCH_ENVIRONMENT.timeOfDay = settings.timeOfDay;
    applyTimeOfDay(renderer, settings.timeOfDay);
    camera.reset();
    hud.localPlayerId = p.assignedPlayerId;
    hud.localTeam = p.team;
    hud.pitchLength = cfg.pitchLength;
    hud.pitchWidth = cfg.pitchWidth;
    hud.halfLengthS = cfg.halfLengthS;
    hud.status = '';
  }

  conn.connect();

  // ── Dev overlay (§15.5) ───────────────────────────────────────────────────
  const readStats = (): DevStats => ({
    fps,
    rttMs: conn.rttMs,
    correctionsPerSec,
    lastErrorM: prediction?.stats.error ?? 0,
    replayedTicks: prediction?.stats.replayed ?? 0,
    interpMode: interpModeName(view.mode),
    snapshotBuffer: interp?.size ?? 0,
    predictedTick: prediction?.world.tick ?? 0,
    serverTick: interp?.newestTick ?? 0,
    // §10 camera debug panel — everything about the LOCAL camera state, read
    // straight off the systems that own it (no sim/wire coupling).
    cameraName: cameraPreset(camera.mode)?.name ?? cameraModeName(camera.mode),
    cameraLookDeg: (input.state.cameraLookYaw * 180) / Math.PI,
    cameraCharging: input.state.isAiming(),
    cameraBlend: camera.pose.offBallBlend,
    cameraFov: camera.pose.fov,
    // The animation the CONTROLLED player is actually playing, read back out of
    // the controller the sim's AnimState drives.
    animCommand:
      (switcher
        ? renderer.playerAnimation?.debugInfo(switcher.controlledId)?.command
        : undefined) ?? '-',
    animContacts: renderer.playerAnimation?.contactsFired ?? 0,
  });
  void mountDevOverlay({
    readStats,
    onRequireOffline: (): void => {
      // Mutating a sim tunable makes this client a different game from the
      // server (§15.2). Say so loudly rather than letting someone chase the
      // resulting "juddering" for a day.
      hud.status = 'Sim tunable changed — this client now disagrees with the server. Reconnect.';
      console.warn(
        '[dev] A sim tunable was changed. NOTE: it is currently INERT (see devtools/overlay.ts) — ' +
          'the sim imports constants directly and does not read TUNABLES.',
      );
    },
  });

  // ── Frame loop ────────────────────────────────────────────────────────────
  let lastFrameMs = performance.now();
  let lastPingMs = 0;
  let tabDownAtMs = -1;
  let elapsedS = 0;

  // Single-chain rAF scheduling: frame() may also be driven directly by the
  // dev hidden-tab driver below, and an unguarded self-rAF inside frame() would
  // spawn one extra permanent rAF chain per driven call.
  let rafPending = false;
  const scheduleFrame = (): void => {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame((t) => {
      rafPending = false;
      frame(t);
    });
  };

  const frame = (nowMs: number): void => {
    scheduleFrame();

    const dtMs = Math.min(nowMs - lastFrameMs, 250);
    lastFrameMs = nowMs;
    const dtS = dtMs / 1000;
    elapsedS += dtS;
    fps += (1000 / Math.max(dtMs, 1) - fps) * 0.1;

    if (nowMs - lastCorrectionWindow >= 1000) {
      correctionsPerSec = corrections;
      corrections = 0;
      lastCorrectionWindow = nowMs;
    }
    if (nowMs - lastPingMs > PING_INTERVAL_MS) {
      lastPingMs = nowMs;
      conn.sendPing(nowMs);
    }

    if (!prediction || !interp || !switcher || !config) {
      renderer.render();
      return;
    }
    const world = prediction.world;
    const localId = switcher.controlledId;
    // Re-read every frame: the settings screen can rebind keys live.
    const controls = input.controlMap;

    if (input.state.consumeMenu()) toggleSettings();

    // ── Tab: tap = switch (§4.8), hold = radar (§4.1) ──────────────────────
    const tabHeld = input.state.isRadarHeld(controls);
    if (tabHeld && tabDownAtMs < 0) tabDownAtMs = nowMs;
    if (!tabHeld && tabDownAtMs >= 0) {
      if (nowMs - tabDownAtMs < TAB_TAP_MS) {
        // The "flick": switch toward where you are steering, or where you are
        // looking if standing still. §4.8's cone selection needs a direction and
        // a keyboard has no right stick.
        const dirX = ctx.rightX * moveX(input, controls) + ctx.forwardX * moveZ(input, controls);
        const dirZ = ctx.rightZ * moveX(input, controls) + ctx.forwardZ * moveZ(input, controls);
        const useAim = Math.hypot(dirX, dirZ) < 0.1;
        const aim = input.state.aimYaw;
        const switched = switcher.requestManual(
          world,
          useAim ? Math.cos(aim) : dirX,
          useAim ? Math.sin(aim) : dirZ,
        );
        if (switched >= 0) {
          pendingFlags |= InputFlags.SwitchPlayer;
          prediction.setLocalPlayer(switched);
        }
      }
      tabDownAtMs = -1;
    }
    hudHandle.setRadarExpanded(tabHeld);
    if (input.state.consumeCameraCycle()) {
      // §10: cycle the three named presets and name the one you landed on.
      const m = camera.cycleMode();
      banners.pushText(`Camera: ${cameraPreset(m)?.name ?? cameraModeName(m)}`);
    }

    // ── Fixed-timestep sim (§3.1) ─────────────────────────────────────────
    // The sim NEVER sees a variable dt. The driver may call this 0..N times.
    // The camera follows its OWN mouse-driven look yaw (§1/§2), NOT the body/aim
    // yaw — feeding aimYaw here is what made WASD drag the camera. Seed the look
    // from the body facing once so the orbit starts behind the player.
    if (!input.state.cameraLookInit) input.state.seedCameraLook(world.players.yaw[localId]!);
    if (!input.state.moveHeadingInit) input.state.seedMoveHeading(world.players.yaw[localId]!);
    const camLookYaw = input.state.cameraLookYaw;
    driver.advance(dtS, () => {
      ctx.possessing = world.players.possessing[localId] === 1;
      ctx.speed = Math.hypot(world.players.vx[localId]!, world.players.vz[localId]!);
      camera.movementBasis(ctx);
      const cmd = buildInputCommand(
        input.state,
        controls,
        ctx,
        prediction!.nextSeq,
        world.tick + 1,
        FIXED_DT * 1000,
      );
      // §4 loose-ball collection assist — steers the input toward a collectable
      // loose ball (shared client seam; the assisted cmd is what we send + predict,
      // so client and server stay in lockstep — it never touches the sim).
      applyCollectionAssist(world, localId, cmd);
      cmd.flags |= pendingFlags;
      pendingFlags = 0;
      // THE CROSSHAIR IS THE AIM (owner: "lets use the cross to decide where the
      // ball will go") — a charging/releasing strike goes down the camera's
      // forward ray, the one the screen-centre reticle marks. Applied here, ahead
      // of predict()/sendInputs(), so client and server strike the same heading.
      // See game/crosshairAim.ts for why aimYaw was NOT already this number.
      applyCrosshairAim(cmd, crosshairYaw, input.state.isShootHeld() || input.state.isPassHeld());
      // §2 pass-to-highlight: point the strike aim AT the highlighted receiver so
      // the authoritative choosePassTarget (client prediction AND server) picks the
      // SAME man the white marker showed — aimYaw is on the wire, so no mismatch.
      if ((cmd.flags & InputFlags.Pass) !== 0 && lastPassTarget >= 0 && world.players.active[lastPassTarget] === 1) {
        const rx = world.players.px[lastPassTarget]! - world.players.px[localId]!;
        const rz = world.players.pz[lastPassTarget]! - world.players.pz[localId]!;
        if (rx * rx + rz * rz > 1e-6) cmd.aimYaw = Math.atan2(rz, rx);
      }
      // §5 middle-mouse team commands (same as practice.ts). Double-click pass-to-
      // me is InputFlags.CallForPass on the wire — already server-authoritative.
      // A single-click CIRCULATE while WE carry plays a safe retain pass now; it
      // predicts locally and its aimYaw/flags ride the wire so the server agrees.
      const mmFeedback = input.state.consumeMiddleFeedback();
      if (mmFeedback) banners.pushText(mmFeedback);
      if (input.state.consumeCirculate() && ctx.possessing) {
        const mate = choosePassTarget(world, localId, cmd.aimYaw);
        if (mate >= 0 && world.players.active[mate] === 1) {
          cmd.flags |= InputFlags.Pass;
          cmd.passCharge = CIRCULATE_PASS_CHARGE;
          const rx = world.players.px[mate]! - world.players.px[localId]!;
          const rz = world.players.pz[mate]! - world.players.pz[localId]!;
          if (rx * rx + rz * rz > 1e-6) cmd.aimYaw = Math.atan2(rz, rx);
        }
      }
      if ((cmd.flags & (InputFlags.Shoot | InputFlags.Pass)) !== 0) lastKickAtMs = nowMs;
      switcher!.noteMovement(cmd.moveX, cmd.moveZ, nowMs);

      // predict() stamps the seq and applies the input immediately (§2.3 step 3)
      // — this is the zero-latency response that makes it feel good.
      const stamped = prediction!.predict(cmd);
      sendWindow.unshift(stamped);
      if (sendWindow.length > INPUT_REDUNDANCY) sendWindow.length = INPUT_REDUNDANCY;
      conn.sendInputs(sendWindow);

      // §11 procedural audio — per TICK, same cadence as every other mount
      // (practice/solo/arena feed it right after step() for the same reason:
      // reading ball state only after driver.advance returns can straddle
      // several ticks). `world` is prediction's local predicted world, so its
      // ball position/velocity is real. `netRuleEvents` holds whatever referee
      // signals arrived off-loop since the last tick (§5.6) — normally empty, and
      // drained here rather than in the transport callback so a whistle is fed to
      // decideAudioFrame on a tick boundary exactly like an offline one.
      audioInput.ballX = world.ball.px; audioInput.ballY = world.ball.py; audioInput.ballZ = world.ball.pz;
      audioInput.ballVX = world.ball.vx; audioInput.ballVY = world.ball.vy; audioInput.ballVZ = world.ball.vz;
      audioInput.phase = world.match.phase as MatchPhase;
      audio.update(audioInput, netRuleEvents);
      if (netRuleEvents.length > 0) netRuleEvents.length = 0;
    });

    prediction.updateVisualSmoothing(dtS);
    switcher.tick(dtMs);

    // ── Remote entities: interpolated 100ms in the past (§2.5) ────────────
    const renderTimeMs = clock.initialized ? clock.renderTimeMs(nowMs) : 0;
    if (clock.initialized) interp.sample(renderTimeMs, view);

    // ── Draw ──────────────────────────────────────────────────────────────
    const p = world.players;
    const localX = p.px[localId]! + prediction.visualOffsetX;
    const localY = p.py[localId]! + prediction.visualOffsetY;
    const localZ = p.pz[localId]! + prediction.visualOffsetZ;

    renderer.beginFrame();
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (i === localId) {
        // The one player we predict. Drawn at the predicted position plus the
        // decaying reconciliation offset, so a correction is never a snap.
        if (p.active[i] !== 1) continue;
        // D9 seam enrichment: pass the sim's real AnimState through so the
        // renderer can play action clips (pass/shoot/tackle/jump/bicycle)
        // instead of only ever inferring locomotion from position deltas —
        // this was the whole reason players never animated (SESSION_LOG
        // session 3/5: the seam existed on both ends but nothing fed it).
        renderer.setPlayer(i, localX, localY, localZ, p.yaw[i]!, p.team[i]!, p.anim[i]!, p.animExecId[i]!, p.vx[i]!, p.vz[i]!);
        continue;
      }
      if (view.mode === InterpMode.Empty) {
        if (p.active[i] !== 1) continue;
        renderer.setPlayer(i, p.px[i]!, p.py[i]!, p.pz[i]!, p.yaw[i]!, p.team[i]!, p.anim[i]!, p.animExecId[i]!, p.vx[i]!, p.vz[i]!);
        continue;
      }
      if (view.active[i] !== 1) continue;
      renderer.setPlayer(i, view.px[i]!, view.py[i]!, view.pz[i]!, view.yaw[i]!, view.team[i]!, view.anim[i]!, view.animExec[i]!, view.vx[i]!, view.vz[i]!);
    }

    // §2.7 — predict the ball only while we own it (or just struck it).
    msSinceLocalKick = lastKickAtMs < 0 ? -1 : nowMs - lastKickAtMs;
    const b = world.ball;
    if (shouldPredictBall(world, localId, msSinceLocalKick) || view.mode === InterpMode.Empty) {
      renderer.setBall(b.px, b.py, b.pz, b.qx, b.qy, b.qz, b.qw, b.powerKick, b.possessedBy);
      hud.ballX = b.px;
      hud.ballZ = b.pz;
    } else {
      // A remote opponent's power kick — decoded off the snapshot wire's
      // BF_POWERKICK bit (net/snapshot.ts) into `view.ballPowerKick`
      // (net/interpolation.ts), owner 2026-08-15: "wire it into online
      // multiplayer". Same red wave a local strike gets. `view.ballPossessedBy`
      // rides the same wire field `ball.possessedBy` (net/snapshot.ts) and gets
      // the same treatment — a remote carrier's dribble gait needs it exactly
      // as much as a local one's.
      renderer.setBall(
        view.ballX, view.ballY, view.ballZ, view.ballQx, view.ballQy, view.ballQz, view.ballQw,
        view.ballPowerKick, view.ballPossessedBy,
      );
      hud.ballX = view.ballX;
      hud.ballZ = view.ballZ;
    }
    // WHOSE HANDS IT IS IN (§4.5, Law 15). Position above is the sim's (or the
    // server's) and stays so; this only names the slot whose animated hand the
    // drawn mesh parents to. Derived off `world` for both branches: it is the
    // predicted world, so it carries the local keeper authoritatively and a
    // remote one through the snapshot's `ball.possessedBy` + `anim`, which is
    // exactly the pair `resolveBallHold` falls back to.
    const hold = resolveBallHold(world);
    renderer.setBallHold(hold.slot, hold.pose);

    // ── Camera (§7) ───────────────────────────────────────────────────────
    // The switch blend feeds the camera a position eased between the player we
    // left and the one we took (§4.8) — control moved instantly, the frame does
    // not.
    switcher.blendedPosition(world, camTarget);
    const camIn: CameraInput = camInput;
    camIn.ballX = hud.ballX; camIn.ballY = b.py; camIn.ballZ = hud.ballZ;
    camIn.ballVX = b.vx; camIn.ballVZ = b.vz;
    camIn.playerX = camTarget.x; camIn.playerY = camTarget.y; camIn.playerZ = camTarget.z;
    camIn.playerYaw = camLookYaw;
    camIn.ballFocus = settings.camera.ballFocus; // §10 opt-in ball framing
    camIn.playerSpeed = Math.hypot(p.vx[localId]!, p.vz[localId]!);
    camIn.sprinting = (p.anim[localId]! as number) === 4; // AnimState.Sprint
    camIn.pitchHalfLength = config.pitchLength * 0.5;
    camIn.pitchHalfWidth = config.pitchWidth * 0.5;
    camIn.elapsedS = elapsedS;
    const pose = camera.update(camIn, dtS);
    renderer.applyCamera(pose);
    // THE AIM — refreshed from the pose actually applied. Online has no local
    // replay/intro cinematic (the server owns time), so this rig is always the
    // one on screen. NaN in a camera the mouse cannot turn (Broadcast/Tele/
    // Dynamic are fixed touchline rigs — see cameraAimsWithCrosshair), which
    // disables the feature coherently: no aim override, no reticle.
    crosshairYaw = cameraAimsWithCrosshair(camera.mode) ? crosshairAimYaw(pose) : Number.NaN;

    renderer.setControlled(localX, localY, localZ, switcher.blend, elapsedS, input.state.aimYaw);
    // The boost aura, on the local player only. `input.state.boostTier` is the
    // LOCAL latch; a remote player's tier is not on the wire (Boost1/2 are
    // per-tick intent flags, never stored on WorldState), so drawing one for him
    // would mean inventing sim state. See boostAura.ts's header.
    renderer.setBoostAura(localId, localX, localY, localZ, input.state.boostTier);
    // A6 §8: an on-pitch direction arrow may appear ONLY with controlled
    // possession — the proximity-gated ball locator showed exactly when the
    // ball was loose / in flight / at another player, so it no longer renders
    // (the aim arrow, gated by possessionArrowVisible, is the one arrow).
    renderer.setBallArrow(false, localX, localY, localZ, pose.ballArrowYaw);

    // ── Pass-target indicator (§4.2, 3C) — the SAME shared scorer the sim
    // strikes with, run on the predicted world; hidden unless a pass charges.
    //
    // GATED ON POSSESSION (owner playtest: "show pass assist only when player
    // has the ball"). Holding pass with a loose ball, a ball in flight, or the
    // ball at another player used to light a receiver ring anyway — an assist
    // marker for a pass that cannot happen. It is the same §6 rule the kick UI
    // gates on, so ownership reads identically for both.
    const hasBall = possessionArrowVisible(world, localId);
    hud.passTargetId = -1; // radar seam: reset each frame, set below if a candidate exists
    if (input.state.isPassHeld() && hasBall) {
      // Thread the last highlight for a steady white marker under aim wobble (§13).
      // Scored along the CROSSHAIR heading — the same number the strike uses, so
      // the highlighted man is the man the ball reaches (the highlight≠strike
      // mismatch fb390d9 fixed, kept fixed from the other side).
      const passAimYaw = Number.isFinite(crosshairYaw) ? crosshairYaw : input.state.aimYaw;
      const passTarget = choosePassTarget(world, localId, passAimYaw, lastPassTarget);
      lastPassTarget = passTarget;
      if (passTarget >= 0) {
        renderer.setPassTarget(p.px[passTarget]!, p.py[passTarget]!, p.pz[passTarget]!, passTarget);
        hud.passTargetId = passTarget; // radar seam: same target the sim would strike to
      }
    } else {
      lastPassTarget = -1; // not charging → next pass selects fresh
    }

    // ── Charge read (LIVE FOOTBALL POLISH §3) — REQUESTED power (charge
    // fraction), never ball speed. Shared by the HUD power meter below and
    // the aim-arrow's charge-intensity feed; at most one of shoot/pass can be
    // held at once (see InputState's charge model), so this is unambiguous.
    let chargeKind: 'shoot' | 'pass' | null = null;
    let chargeFraction = 0;
    if (input.state.isShootHeld()) {
      chargeKind = 'shoot';
      chargeFraction = input.chargeFraction('shoot', SHOT_CHARGE_MIN_S, SHOT_CHARGE_MAX_S);
    } else if (input.state.isPassHeld()) {
      chargeKind = 'pass';
      chargeFraction = input.chargeFraction('pass', PASS_CHARGE_MIN_S, PASS_CHARGE_MAX_S);
    }

    // ── Kick UI gate (§4/§5) — the POSSESSION gate: does the local player truly
    // own the ball in live play (§6 shared rule). The power meter needs it, and
    // it ARMS the reticle (full opacity + the ball-destination dot) without
    // deciding whether the reticle is drawn at all — see hud.crosshairShow.
    // (Online has no local pause — the server owns time — so possession is the
    // whole gate here; §4's pause-cancel case lives in practice.ts.)
    //
    // The FEET ARROW that used to ride this gate is gone (owner: *"dont show the
    // arrows near his foot"*); render/indicators.ts draws neither it nor the 7 m
    // pitch marker, so there is nothing left to feed.
    const canShowKickUi = hasBall;

    // ── HUD (read-only: the HUD redraws on its own timers, §1.1) ──────────
    hud.scoreA = world.match.scoreA;
    renderer.setScoreboard(hud.nameA, hud.nameB, world.match.scoreA, world.match.scoreB,
      world.match.clockTicks / 60,
      (world.match.phase as number) === (MatchPhase.HalfTime as number) ? 'HT'
        : (world.match.phase as number) === (MatchPhase.FullTime as number) ? 'FT'
        : world.match.half >= 1 ? '2ND' : '1ST');
    hud.scoreB = world.match.scoreB;
    hud.clockTicks = world.match.clockTicks;
    hud.addedTimeTicks = world.match.addedTimeTicks;
    hud.half = world.match.half;
    hud.phase = world.match.phase as MatchPhase;
    hud.restartTaker = world.match.restartTaker; // ui/banners.ts restart caption seam
    configureMatchNames(world.config.squadSize); // no-op unless the size actually changed
    hud.localPlayerId = localId;
    hud.teamAAttacksPositiveX = world.teamAAttacksPositiveX; // radar seam: mirroring input
    hud.rttMs = conn.rttMs;
    hud.fps = fps;
    hud.chargeShow = canShowKickUi && chargeKind !== null;
    if (chargeKind !== null) hud.chargeKind = chargeKind;
    hud.chargeFraction = chargeFraction;
    // Screen-centre aiming crosshair — ALWAYS ON (owner: *"keep the cross air
    // always on the screen"*). Online has no local pause, full-time board,
    // intro or replay to exclude (the server owns time, see canShowKickUi's own
    // comment), so the only remaining gate is the player's own Settings > HUD
    // toggle. Possession moved to `crosshairArmed`: the reticle is present
    // without the ball, dimmed and with no destination dot.
    // The settings overlay is the one "menu" online has (an online match cannot
    // freeze, §9.3, so it overlays without pausing) — a reticle sitting on top
    // of it is exactly the case "always on screen" must not be read to include,
    // and the pointer is unlocked there so there is no aiming going on anyway.
    // `!aimable` joins the settings overlay as a hide-case — a reticle in a
    // fixed-azimuth camera cannot steer the ball, so drawing it would lie.
    const interrupted = settingsScreen !== null || !Number.isFinite(crosshairYaw);
    hud.crosshairShow = crosshairShown(settings.crosshairEnabled, interrupted);
    hud.crosshairArmed = crosshairArmed(settings.crosshairEnabled, interrupted, canShowKickUi);
    hud.crosshairSize = settings.crosshairSize;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      const useView = i !== localId && view.mode !== InterpMode.Empty;
      hud.px[i] = useView ? view.px[i]! : p.px[i]!;
      hud.pz[i] = useView ? view.pz[i]! : p.pz[i]!;
      hud.active[i] = useView ? view.active[i]! : p.active[i]!;
      hud.team[i] = useView ? view.team[i]! : p.team[i]!;
      hud.yaw[i] = useView ? view.yaw[i]! : p.yaw[i]!; // radar seam: facing arrow
    }

    renderer.render();
  };

  // Camera scratch — hoisted so the frame loop allocates nothing (§15.7).
  const camTarget = { x: 0, y: 0, z: 0 };
  const camInput: CameraInput = {
    ballX: 0, ballY: 0, ballZ: 0, ballVX: 0, ballVZ: 0,
    playerX: 0, playerY: 0, playerZ: 0, playerYaw: 0,
    playerSpeed: 0, sprinting: false,
    pitchHalfLength: 52.5, pitchHalfWidth: 34, elapsedS: 0,
  };
  // §11 audio scratch — hoisted, zero per-tick allocation (§15.7).
  const audioInput: AudioTickInput = {
    dt: FIXED_DT,
    ballX: 0, ballY: 0, ballZ: 0,
    ballVX: 0, ballVY: 0, ballVZ: 0,
    phase: MatchPhase.Warmup,
    pitchHalfLength: 52.5,
  };

  // D0: the EA-FC broadcast look is the DEFAULT — defaultSettings() ships
  // CameraMode.Broadcast; this only differs if the player chose otherwise.
  camera.setMode(settings.camera.mode);
  console.info(`[fg] protocol v${PROTOCOL_VERSION}, camera ${cameraModeName(camera.mode)}`);
  scheduleFrame();

  // DEV ONLY: keep the loop alive while the tab is hidden. Chrome parks rAF on
  // hidden tabs, which froze every headless-QA capture at a stale frame (the
  // interp buffer kept filling but nothing sampled it). Visible tabs are pure
  // rAF, unchanged.
  //
  // Driven by a WORKER, not setInterval. Hidden-tab timers are throttled to
  // ~1 Hz, so the setInterval version of this pump delivered exactly 1 fps —
  // enough to keep clock sync truthful, but useless for the thing it exists
  // for. Anything that only happens in real time was unverifiable: a match
  // could not be played through kickoff → goal → restart, animation could not
  // be judged at speed, camera jitter could not be seen, and any frame-rate
  // measured this way was a measurement of the throttle, not of the game.
  // Worker timers are NOT throttled, and WebGL still renders while hidden (only
  // the compositor is idle), so this restores real-time headless QA.
  if (import.meta.env.DEV) {
    const tick = (): void => {
      const now = performance.now();
      if (document.hidden && now - lastFrameMs > 8) frame(now);
    };
    try {
      const src = 'setInterval(()=>postMessage(0),16)';
      const url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
      new Worker(url).onmessage = tick;
    } catch {
      // No Worker (or a CSP that blocks blob workers) — fall back to the old
      // 1 Hz pump rather than leaving a hidden tab frozen outright.
      setInterval(tick, 250);
    }
  }
}

/** Raw WASD axes, for the §4.8 switch flick direction. */
function moveX(input: InputManager, map: ControlMap): number {
  return (
    (input.state.actionHeld(map, 'moveRight') ? 1 : 0) - (input.state.actionHeld(map, 'moveLeft') ? 1 : 0)
  );
}
function moveZ(input: InputManager, map: ControlMap): number {
  return (
    (input.state.actionHeld(map, 'moveForward') ? 1 : 0) - (input.state.actionHeld(map, 'moveBack') ? 1 : 0)
  );
}

// ---------------------------------------------------------------------------
// Entry
// ---------------------------------------------------------------------------

/** Title flow (title → menu → modes → setup). Re-entered when a match quits. */
/**
 * Mount the Test Arena full-screen over `uiEl`, returning to the menu's MODES
 * screen on exit. Used both by the menu card (onStartArena) and the `#arena`
 * deep link. The arena owns the production sim/render/input stack; we only wire
 * its lifecycle and the Back path.
 */
function openArena(sceneEl: HTMLElement, uiEl: HTMLElement, settings: GameSettings): void {
  uiEl.innerHTML = '';
  const arenaRoot = document.createElement('div');
  arenaRoot.style.cssText = 'position:fixed;inset:0;z-index:20;';
  uiEl.appendChild(arenaRoot);
  const backToModes = (): void => {
    arenaRoot.remove();
    uiEl.innerHTML = '';
    if (window.location.hash) window.location.hash = '';
    openMenu(sceneEl, uiEl, settings, 'modes');
  };
  void import('./game/testArena.js')
    .then(({ mountTestArena }) => mountTestArena(arenaRoot, { onExit: backToModes }))
    .catch((err: unknown) => {
      console.error('[fg] test arena failed to boot:', err);
      backToModes();
    });
}

/**
 * Mount Solo Free Practice full-screen — a dedicated training mode (just the human
 * outfield player, a ball and a keeper, no match rules). Reuses the production
 * render/sim/input stack; only the training config differs. Same lifecycle/Back
 * path as the arena; dynamically imported so it stays out of the menu bundle.
 */
function openSolo(sceneEl: HTMLElement, uiEl: HTMLElement, settings: GameSettings): void {
  uiEl.innerHTML = '';
  const soloRoot = document.createElement('div');
  soloRoot.style.cssText = 'position:fixed;inset:0;z-index:20;';
  uiEl.appendChild(soloRoot);
  const backToModes = (): void => {
    soloRoot.remove();
    uiEl.innerHTML = '';
    if (window.location.hash) window.location.hash = '';
    openMenu(sceneEl, uiEl, settings, 'modes');
  };
  void import('./game/soloPractice.js')
    .then(({ mountSoloPractice }) => mountSoloPractice(soloRoot, { settings, onExit: backToModes }))
    .catch((err: unknown) => {
      console.error('[fg] solo practice failed to boot:', err);
      backToModes();
    });
}



function openMenu(
  sceneEl: HTMLElement,
  uiEl: HTMLElement,
  settings: GameSettings,
  startAt: StartScreen = 'title',
): void {
  let menuSettings: SettingsScreen | null = null;
  // Plays for as long as the menu is on screen; every flow.dispose() call
  // below (a match starting, a lobby join succeeding) pairs with a
  // menuMusic.dispose() right beside it so the track never survives into a
  // match. A fresh mountMenuMusic() per openMenu() call rather than one
  // shared instance across the app's lifetime — openMenu() is called again
  // on every return to the menu, and re-mounting is what gives it the
  // fade-in back in rather than silence resuming mid-loop.
  const menuMusic = mountMenuMusic(settings.musicVolume, settings.masterVolume);
  const flow = mountArenaMenu(uiEl, settings, {
    onStartPractice: (setup): void => {
      flow.dispose();
      menuMusic.dispose();
      // Career / World Cup route THROUGH this same handler — they only tag the
      // setup with a CompetitionContext (ui/tournament-store.ts). Everything
      // below is the ordinary offline practice path; the tag changes the two
      // presentation labels and where the player lands when the match exits.
      const comp = setup.competition;
      // Flow step 5: brief VS card in team colours while the world boots
      // (Rapier WASM + renderer). Practice itself then runs the kickoff intro.
      const vs = mountVsCard(
        uiEl,
        comp?.teamALabel ?? 'YOU',
        comp?.teamBLabel ?? 'COM',
        comp?.label ?? '',
      );
      const shownAt = performance.now();
      // Fully offline — no lobby, no server, no sockets (practice.ts header).
      void startPractice(sceneEl, uiEl, {
        setup,
        settings,
        onQuitToMenu: (result): void => {
          /*
           * PAY FOR THE MATCH. This callback is the one funnel every finished
           * match passes through — practice, career fixture and World Cup tie
           * alike — so wiring the wallet here covers every mode at once rather
           * than once per call site.
           *
           * Abandoned matches pay nothing: `finished` is the same flag the
           * competition store uses to decide whether a scoreline counts, and
           * paying for a quit would make quitting the fastest way to earn.
           */
          awardMatch(result.scoreA, result.scoreB, result.finished);

          if (!comp) {
            openMenu(sceneEl, uiEl, settings);
            return;
          }
          // Only a match played to the final whistle counts. Abandoning at 0-0
          // in the eighth minute must not hand the league a 0-0; the fixture
          // stays live and the player replays it. The token inside `comp`
          // guards the other direction — a result whose fixture has since
          // moved on (reset, second tab) is dropped by the store.
          if (result.finished) reportCompetitionResult(comp, result.scoreA, result.scoreB);
          openMenu(sceneEl, uiEl, settings, comp.kind === 'career' ? 'career' : 'worldcup');
        },
      }).then(() => {
        // Keep the card up just long enough to read, never long enough to bore.
        const remaining = Math.max(0, 900 - (performance.now() - shownAt));
        window.setTimeout(() => vs.dispose(), remaining);
      }).catch((err: unknown) => {
        vs.dispose();
        console.error('[fg] practice failed to boot:', err);
        openMenu(sceneEl, uiEl, settings);
      });
    },
    onQuickMatch: (name): void => void joinFromMenu(name, '', false),
    onCreateFriend: (name, mode): void => void joinFromMenu(name, '', true, mode),
    onJoinCode: (name, code): void => void joinFromMenu(name, code, false),
    onRankedMatch: (name, mode): void => void joinRankedMatch(name, mode),
    onOpenSettings: (): void => {
      if (menuSettings) return;
      // No live hooks from the menu: camera/quality persist and apply at the
      // next match start (settings.ts header). Controls apply via the blob.
      menuSettings = mountSettingsScreen(uiEl, {
        settings,
        onChange: (s) => saveSettings(s),
        onClose: (): void => {
          menuSettings?.dispose();
          menuSettings = null;
        },
        // The one live hook this call passes — camera/quality/input/audio
        // stay commented-out-absent above (they only mean anything mid-match),
        // but menu music IS playing right now, so Master/Music moving the
        // slider here has to reach it immediately rather than waiting for a
        // match that was never going to read this settings object again.
        live: { music: { setVolume: (mv, mav) => menuMusic.setVolume(mv, mav) } },
      });
    },
    onStartArena: (): void => {
      // The Test Arena runs the production stack full-screen. Tear down the menu
      // flow, mount the arena, and on exit re-open the menu at MODES so Back
      // lands on Select Mode (not the title). Same dynamic import as the #arena
      // deep-link path, so the heavy game code never joins the menu bundle.
      flow.dispose();
      menuMusic.dispose();
      openArena(sceneEl, uiEl, settings);
    },
    onStartSolo: (): void => {
      flow.dispose();
      menuMusic.dispose();
      openSolo(sceneEl, uiEl, settings);
    },
    persist: (): void => saveSettings(settings),
  }, startAt);

  async function joinFromMenu(
    name: string,
    code: string,
    createPrivate: boolean,
    mode = '5v5',
  ): Promise<void> {
    settings.playerName = name;
    saveSettings(settings);
    flow.setBusy(true);
    flow.setStatus(code ? `Joining ${code}…` : createPrivate ? 'Creating match…' : 'Finding a match…');
    try {
      if (code || createPrivate) {
        // Friend-match path: waiting room → admin START → seat pick → handoff
        // (docs/ROOM-LOBBY-SPEC.md). Quick-match below stays instant.
        const seat = await joinWaitingRoom(name, code, createPrivate, mode);
        flow.dispose();
        menuMusic.dispose();
        const handoff = await runRoomLobby(uiEl, lobbyUrl().replace(/\/+$/, ''), seat.roomId, seat.playerId);
        if (!handoff) { window.location.href = window.location.pathname; return; } // left the room
        // The room just released everyone at once — put the loading card up
        // straight away, not after the warm-connect grace: this player was on
        // a busy screen a moment ago and a black frame here reads as a crash.
        await startMatch(sceneEl, uiEl, handoff, settings, { fromRoom: true });
        return;
      }
      const handoff = await fetchHandoff(name);
      flow.dispose();
      menuMusic.dispose();
      await startMatch(sceneEl, uiEl, handoff, settings);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      flow.setBusy(false);
      flow.setStatus(`Could not join: ${msg}`);
      console.error(err);
    }
  }

  /** Rated matchmaking (docs/ACCOUNTS-SPEC.md phase 3) — join the queue, then
   *  hand off to runMatchmakingSearch()'s own screen once a ticket exists. */
  async function joinRankedMatch(name: string, mode: '11v11' | '5v5'): Promise<void> {
    settings.playerName = name;
    saveSettings(settings);
    flow.setBusy(true);
    flow.setStatus(`Joining the ${mode} ranked queue…`);
    try {
      const base = lobbyUrl().replace(/\/+$/, '');
      // Signed in ⇒ the lobby resolves a real rating for this queue entry
      // (never trusted from here — see rating-lookup.ts). Guests queue at the
      // shared default.
      const accessToken = await getAccessToken();
      const res = await fetchAwake(`${base}/matchmaking/join`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ mode, playerName: name, accessToken }),
      });
      const text = await res.text();
      const data: unknown = text ? JSON.parse(text) : {};
      if (!res.ok) {
        const msg =
          typeof data === 'object' && data !== null && typeof (data as { error?: unknown }).error === 'string'
            ? (data as { error: string }).error
            : `HTTP ${res.status}`;
        throw new Error(`ranked queue join failed: ${msg}`);
      }
      const { ticketId } = data as { ticketId: string };
      flow.dispose();
      menuMusic.dispose();
      const handoff = await runMatchmakingSearch(uiEl, base, ticketId, mode, name);
      if (!handoff) { window.location.href = window.location.pathname; return; } // cancelled
      await startMatch(sceneEl, uiEl, handoff, settings);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      flow.setBusy(false);
      flow.setStatus(`Could not join ranked queue: ${msg}`);
      console.error(err);
    }
  }
}

async function main(): Promise<void> {
  const sceneEl = document.getElementById('scene');
  const uiEl = document.getElementById('ui');
  if (!sceneEl || !uiEl) throw new Error('missing #scene / #ui — check index.html');

  const settings = loadSettings();
  // TUNABLES.camera is read live by render/camera.ts — apply the persisted rig
  // before anything constructs a camera (§7.1).
  applyCameraSettings(settings);

  // §9.4 — SACRED: a `?join=CODE` / `/j/CODE` deep link never sees the menu.
  // One name field, then straight into the match. Guard it fiercely.
  const deepCode = joinCodeFromUrl();
  if (deepCode) {
    const name = await mountNamePrompt(uiEl, settings.playerName, deepCode);
    settings.playerName = name;
    saveSettings(settings);
    try {
      // Invite links land in the same waiting room as the creator (§9.4's
      // no-menu guarantee holds: the lobby screen is the room, not the menu).
      const seat = await joinWaitingRoom(name, deepCode, false);
      const handoff = await runRoomLobby(uiEl, lobbyUrl().replace(/\/+$/, ''), seat.roomId, seat.playerId);
      if (!handoff) { window.location.href = window.location.pathname; return; }
      await startMatch(sceneEl, uiEl, handoff, settings);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error(err);
      uiEl.innerHTML =
        `<div style="position:fixed;inset:0;display:grid;place-items:center;background:#0d1117;` +
        `color:#f66;font-family:system-ui,sans-serif;text-align:center">` +
        `<div>Could not join: ${msg}<br><br>` +
        `<button id="fg-retry" style="padding:9px 18px;border-radius:4px;border:0;background:#ffd94a;font-weight:700;cursor:pointer">RETRY</button>` +
        `&nbsp;<button id="fg-menu" style="padding:9px 18px;border-radius:4px;border:1px solid #666;background:none;color:#fff;cursor:pointer">MENU</button>` +
        `</div></div>`;
      document.getElementById('fg-retry')?.addEventListener('click', () => window.location.reload());
      document.getElementById('fg-menu')?.addEventListener('click', () => {
        uiEl.innerHTML = '';
        openMenu(sceneEl, uiEl, settings);
      });
    }
    return;
  }

  // §6 Test Arena — reachable both from the MODES card (onStartArena) and this
  // `#arena` deep link. Both go through openArena, which mounts the production
  // stack and, on exit/Back, returns to Select Mode. Dynamically imported so the
  // heavy game code stays out of the menu bundle.
  if (window.location.hash.replace('#', '') === 'arena') {
    openArena(sceneEl, uiEl, settings);
    return;
  }

  // Solo Free Practice — MODES card (onStartSolo) or the `#solo` deep link.
  if (window.location.hash.replace('#', '') === 'solo') {
    openSolo(sceneEl, uiEl, settings);
    return;
  }

  openMenu(sceneEl, uiEl, settings);
}

void main();
